import { Message } from '@/types/converse/message';
import ListAltIcon from '@mui/icons-material/ListAlt';
import { Box, Button, Tooltip } from '@mui/material';
import JSZip from 'jszip';
import React, { useState } from 'react';
import FileModal from './ConverseClone/messages/file-modal';

interface ChatDownloaderProps {
  messages: Message[];
  chatId?: string;
}

function ChatDownloader({
  messages,
  chatId,
}: ChatDownloaderProps): React.ReactNode {
  const [modalOpen, setModalOpen] = useState(false);
  const [isDownloading, setIsDownloading] = useState<boolean>(false);

  // Helper function to extract file URLs from all messages
  const extractFiles = (messages: Message[]): string[] => {
    const fileUrls: string[] = [];
    messages.forEach((message) => {
      if (message.steps) {
        message.steps.forEach((step) => {
          if (step.type === 'data_update_file' && step.content && !step.error) {
            if (typeof step.content === 'string') {
              fileUrls.push(step.content);
            }
          }
        });
      }
    });
    return [...new Set(fileUrls)]; // Remove duplicates
  };

  // Helper function to download a file and return its content
  const downloadFile = async (
    url: string
  ): Promise<{ filename: string; content: ArrayBuffer }> => {
    try {
      const response = await fetch(url);
      const blob = await response.blob();
      const arrayBuffer = await blob.arrayBuffer();
      const filename = url.split('/').pop() || 'unknown_file';
      return { filename, content: arrayBuffer };
    } catch (error) {
      console.error('Error downloading file:', error);
      throw error;
    }
  };

  // Convert messages to text format
  const convertMessagesToText = (messages: Message[]): string => {
    return messages
      .map((msg) => {
        const timestamp = new Date().toLocaleString();
        const role = msg.role === 'assistant' ? 'Assistant' : 'User';
        return `[${timestamp}] ${role}:\n${msg.message}\n${'-'.repeat(50)}\n`;
      })
      .join('\n');
  };

  // Handle export of entire chat with files as a zip
  const handleExport = async () => {
    if (messages.length === 0) return;
    setIsDownloading(true);

    try {
      const zip = new JSZip();
      const timestamp = new Date().toISOString().split('T')[0];

      // Add all messages as a single text file
      const chatContent = convertMessagesToText(messages);
      zip.file('conversation.txt', chatContent);

      // Get all file URLs from all messages
      const fileUrls = extractFiles(messages);

      // Download and add all files
      if (fileUrls.length > 0) {
        const filesFolder = zip.folder('files');
        if (filesFolder) {
          const fileDownloads = fileUrls.map(async (url) => {
            const { filename, content } = await downloadFile(url);
            filesFolder.file(filename, content);
            return filename;
          });

          await Promise.all(fileDownloads);
        }
      }

      // Generate and download the zip file
      const content = await zip.generateAsync({ type: 'blob' });
      const url = URL.createObjectURL(content);
      const link = document.createElement('a');
      link.href = url;
      const fileName = chatId
        ? `chat_${chatId}_${timestamp}.zip`
        : `chat_export_${timestamp}.zip`;
      link.download = fileName;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      URL.revokeObjectURL(url);
    } catch (error) {
      console.error('Failed to export chat:', error);
    } finally {
      setIsDownloading(false);
    }
  };

  return (
    <Box sx={{ display: 'inline-flex', alignItems: 'center', mr: 5 }}>
      <Tooltip title="View All files">
        <Button
          onClick={() => {
            setModalOpen(true);
          }}
          variant="text"
        >
          <ListAltIcon />
        </Button>
      </Tooltip>
      {/* <Tooltip title="Download conversation and files">
        <span>
          <IconButton
            onClick={handleExport}
            disabled={isDownloading || messages.length === 0}
            size="small"
            sx={{
              backgroundColor: 'rgba(255, 154, 53, 0.1)',
              '&:hover': {
                backgroundColor: 'rgba(255, 154, 53, 0.2)',
              },
              '&.Mui-disabled': {
                backgroundColor: 'rgba(0, 0, 0, 0.1)',
                color: 'rgba(0, 0, 0, 0.3)',
              },
            }}
          >
            {isDownloading ? (
              <CircularProgress size={20} />
            ) : (
              <FileDownloadOutlined />
            )}
          </IconButton>
        </span>
      </Tooltip> */}
      <FileModal
        urls={extractFiles(messages)}
        modalOpen={modalOpen}
        onClose={() => {
          setModalOpen(false);
        }}
        onChipClick={(url) => {
          downloadFile(url);
        }}
      />
    </Box>
  );
}

export default ChatDownloader;
